import { computed } from 'vue';
import { useAvailability } from '@nuxt-common/media-card-large/app/composables/useAvailability';
import { useTraits } from '@nuxt-common/media-card-large/app/composables/useTraits';
import { useInitSize } from '@nuxt-common/media-card-large/app/modules/size';
import { useProductType } from '~/composables/useProductType';
import { useBrand } from '~/composables/useBrand';
import { toTraitId } from '../utils/utils';
import type { ParsedSizes, BaseSize, Size, SizeProps, SelectedSizeId } from '~/declaration/traits';
import type { ParsedLatchkeys } from '../declaration/latchkey';

function toBaseSize(size: Size, availability: Record<string, boolean>): BaseSize {
  return {
    id: size.id,
    name: size.name,
    displayName: size.displayName,
    available: availability[size.id],
  };
}

function useInitEachSize(id: string, latchkeyNames: string[], latchkeySizeMap: Record<string, ParsedSizes>) {
  latchkeyNames.forEach((latchkeyName) => {
    const sizes = latchkeySizeMap[latchkeyName];
    useInitSize({ id: toTraitId(id, latchkeyName), data: sizes });
  });
}

export default function useLatchkeySize(
  id: string,
  selectedLatchkey: Ref<string>,
  { latchkeyNames, latchkeySizeMap }: ParsedLatchkeys,
): {
  selectedSizeId: SelectedSizeId;
  selectedSize: WritableComputedRef<Size | null>;
  sizeProps: ComputedRef<SizeProps>;
  sizes: ComputedRef<Size[]>;
} {
  const { isMcom } = useBrand();
  const { isConsolidatedLatchKey } = useProductType();

  if (isConsolidatedLatchKey) {
    useInitEachSize(id, latchkeyNames, latchkeySizeMap);

    const traitId = computed(() => toTraitId(id, selectedLatchkey.value));

    /**
     * @type {Record<string, { traits: ReturnType<typeof useTraits>, availability: ReturnType<typeof useAvailability>}>}
     */
    const sizesTraits = Object.keys(latchkeySizeMap).reduce((acc, latchkey) => {
      const tId = toTraitId(id, latchkey);
      acc[tId] = {
        traits: useTraits(tId),
        availability: useAvailability(tId),
      };
      return acc;
    }, {});

    const selectedSize = computed({
      get() {
        return sizesTraits[traitId.value]?.traits.selectedSize.value ?? null;
      },
      set(newValue) {
        sizesTraits[traitId.value].traits.selectedSize.value = newValue;
      },
    });

    const selectedSizeId = computed({
      get() {
        return selectedSize.value?.id ?? null;
      },
      set(newValue) {
        sizesTraits[traitId.value].traits.selectedSizeId.value = newValue;
      },
    });

    const availability = computed(() => sizesTraits[traitId.value]?.availability.size.value);

    const sizeArray = computed(() => {
      const sizeData = latchkeySizeMap[selectedLatchkey.value || ''] || null;
      return sizeData?.sizeArray || [];
    });

    const sizes = computed(() => {
      const sizeData = latchkeySizeMap[selectedLatchkey.value || ''] || null;
      return sizeData?.sizeArray.map((size) => toBaseSize(size, availability.value)) || [];
    });

    return {
      sizes: sizeArray,
      selectedSize,
      selectedSizeId,
      sizeProps: computed(() => ({
        latchkeys: latchkeyNames,
        sizes: sizes.value,
        isSizeDropdown: true,
        // dropdownOptional default value -> true
        ...(isMcom && { dropdownOptional: false }),
      })),
    };
  }

  return {
    sizes: computed(() => []),
    selectedSize: computed(() => null),
    selectedSizeId: computed(() => null),
    sizeProps: computed(() => ({
      latchkeys: [],
      sizes: [],
      isSizeDropdown: false,
    })),
  };
}
